<template>
  <div
    class="select-scroll-wrapper"
    :class="{
      'select-scroll-focus':
        isFocused ||
        (!!internalValue &&
          (Array.isArray(internalValue) ? internalValue.length > 0 : true)),
      'select-scroll-error': props.isError,
      'select-scroll-disabled': props.disabled,
      'select-scroll-hide-dropdown': !props.isShowDropdownIcon,
      'select-scroll-search-icon': props.isSearchIcon,
      'select-scroll-no-prefix-icon': props.isNoPrefixIcon,
    }"
  >
    <v-select
      ref="selectRef"
      v-model="internalValue"
      class="select-scroll"
      :options="props.options"
      :label="props.labelKey"
      :placeholder="!isFocused ? props.label : ''"
      :multiple="props.multiple"
      :searchable="true"
      :filterable="false"
      :clearable="props.isClearable"
      :disabled="props.disabled"
      :selectable="isOptionSelectable"
      @focusin="handleFocus"
      @focusout="handleBlur"
      @update:modelValue="handleChange"
      @search="handleSearch"
      @open="onOpen"
      @close="onClose"
    >
      <template #option="option">
        <div
          v-if="option.isGroup"
          class="scroll-option-group-label"
          @mousedown.prevent
        >
          {{ option.text }}
        </div>
        <div v-else class="scroll-option-item">
          <img
            v-if="option.icon"
            :src="option.icon"
            class="scroll-option-icon"
            alt="icon"
          />
          <span class="scroll-option-text">{{ option.text }}</span>
        </div>
      </template>

      <template #selected-option="option">
        <div class="scroll-selected-item">
          <img
            v-if="option.icon"
            :src="option.icon"
            class="scroll-selected-icon"
            alt="icon"
          />
          <span>{{ option.text }}</span>
        </div>
      </template>

      <!-- Footer แสดงสถานะ -->
      <template #list-footer>
        <div v-if="props.isLoading" class="vs-footer-loading">Loading...</div>
        <div v-else-if="props.hasMore" class="vs-footer-loadmore">
          <!-- Scroll to load more -->
        </div>
      </template>

      <template #open-indicator="{ attributes }">
        <span v-bind="attributes">
          <img
            class="cursor-pointer"
            src="/images/icons/ic-indicator-thin.svg"
          />
        </span>
      </template>
    </v-select>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onBeforeUnmount } from 'vue';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

const props = defineProps({
  modelValue: {
    type: [Array, String, Number, Object, null],
    default: () => [],
  },
  options: {
    type: Array,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  labelKey: {
    type: String,
    default: 'text',
  },
  valueKey: {
    type: [Array, String, Number, Object, null],
    default: null,
  },
  multiple: {
    type: Boolean,
    default: false,
  },

  isError: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },

  isShowDropdownIcon: {
    type: Boolean,
    default: true,
  },
  isClearable: {
    type: Boolean,
    default: true,
  },
  isSearchIcon: {
    type: Boolean,
    default: false,
  },
  isNoPrefixIcon: {
    type: Boolean,
    default: false,
  },
  isIconOption: {
    type: Boolean,
    default: false,
  },

  // ===== Scroll Load =====
  hasMore: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  debounceMs: {
    type: Number,
    default: 400,
  },

  // callbacks
  onChange: {
    type: Function,
    default: () => {},
  },
  onSearch: {
    type: Function,
    default: async (_keyword) => {},
  },
  onLoadMore: {
    type: Function,
    default: async (_keyword) => {},
  },
});

const emit = defineEmits(['update:modelValue']);

const selectRef = ref(null);
const internalValue = ref(props.multiple ? [] : null);
const isFocused = ref(false);

const hasOpenedOnce = ref(false);

const lastKeyword = ref('');
let debounceTimer = null;

// สำหรับ scroll listener
let menuEl = null;
let rafLock = false;

const reduceFn = (option) => option?.value;

const isOptionSelectable = (option) => !option?.isGroup;

const handleFocus = () => {
  if (props.disabled) return;
  isFocused.value = true;

  const wrapper = selectRef.value?.$el?.closest?.('.select-scroll-wrapper');
  const dropdownToggle = wrapper?.querySelector?.('.vs__dropdown-toggle');
  const dropdownMenu = wrapper?.querySelector?.('.vs__dropdown-menu');
  if (dropdownToggle)
    dropdownToggle.classList.add('select-scroll-dropdown-toggle-focus');
  if (dropdownMenu) dropdownMenu.classList.add('select-scroll-dropdown-menu-focus');
  if (props.isError && dropdownToggle)
    dropdownToggle.classList.remove('select-scroll-dropdown-toggle-focus');
};

const handleBlur = () => {
  setTimeout(() => {
    if (props.disabled) return;
    isFocused.value = false;

    const wrapper = selectRef.value?.$el?.closest?.('.select-scroll-wrapper');
    const dropdownToggle = wrapper?.querySelector?.('.vs__dropdown-toggle');
    const dropdownMenu = wrapper?.querySelector?.('.vs__dropdown-menu');
    if (dropdownToggle)
      dropdownToggle.classList.remove('select-scroll-dropdown-toggle-focus');
    if (dropdownMenu)
      dropdownMenu.classList.remove('select-scroll-dropdown-menu-focus');
    if (props.isError && dropdownToggle)
      dropdownToggle.classList.remove('select-scroll-dropdown-toggle-focus');
  }, 100);
};

const handleChange = (value) => {
  props.onChange(value);
};

const handleSearch = (keyword) => {
  lastKeyword.value = keyword ?? '';

  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(async () => {
    await props.onSearch(lastKeyword.value);
  }, props.debounceMs);
};

const onMenuScroll = async (e) => {
  // กัน scroll event ถี่มาก
  if (rafLock) return;
  rafLock = true;
  requestAnimationFrame(() => {
    rafLock = false;
  });

  if (props.isLoading) return;
  if (!props.hasMore) return;

  const el = e.target;
  const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;

  if (nearBottom) {
    // เก็บ scroll position ก่อนโหลด
    const scrollTop = el.scrollTop;
    await props.onLoadMore(lastKeyword.value);
    
    // คืน scroll position หลังโหลด
    await nextTick();
    if (menuEl) {
      menuEl.scrollTop = scrollTop;
    }
  }
};

const onOpen = async () => {
  await nextTick();

  // หา dropdown menu ของ instance นี้
  const root = selectRef.value?.$el;
  const wrapper = root?.closest?.('.select-scroll-wrapper');
  menuEl = wrapper?.querySelector?.('.vs__dropdown-menu') || null;

  if (menuEl) {
    menuEl.addEventListener('scroll', onMenuScroll, { passive: true });
  }

  // ✅ เปิดครั้งแรก → เรียก API ทันที
  if (!hasOpenedOnce.value && !props.options?.length && !props.isLoading) {
    hasOpenedOnce.value = true;
    lastKeyword.value = '';
    await props.onSearch('');
  }
};

const onClose = () => {
  if (menuEl) {
    menuEl.removeEventListener('scroll', onMenuScroll);
    menuEl = null;
  }
};

// ===== sync value =====
watch(
  () => props.modelValue,
  (newValue) => {
    if (props.multiple) {
      internalValue.value = Array.isArray(newValue) ? [...newValue] : [];
    } else {
      internalValue.value = newValue ?? null;
    }
  },
  { immediate: true, deep: true }
);

watch(
  () => props.valueKey,
  (newValue) => {
    internalValue.value = props.multiple
      ? Array.isArray(newValue)
        ? [...newValue]
        : []
      : newValue ?? null;
  },
  { immediate: true, deep: true }
);

onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer);
  onClose();
});
</script>

<style lang="scss">
.select-scroll-wrapper {
  text-align: left;
  position: relative;
  width: 100%;
  margin-top: 10px;

  &.select-scroll-search-icon {
    .select-scroll {
      .vs__selected-options {
        padding: 2px 7px;

        .vs__selected {
          margin: 8px 4px 0 32px !important;
          border: none;
        }

        .vs__search {
          padding-left: 32px;
          margin: 0;
          color: $semantic-color-text-form-placeholder;
        }

        &:after {
          position: absolute;
          content: '';
          background-color: transparent;
          background-size: cover;
          width: 19.5px;
          height: 19.5px;
          margin-top: -1px;
          background-repeat: no-repeat;
          background-image: url('/images/icons/ic-search-rounded-purple.svg');
          top: 75%;
          transform: translateY(-75%);
        }
      }
    }
  }

  &.select-scroll-no-prefix-icon {
    .select-scroll {
      .vs__selected-options {
        .vs__selected {
          margin: 8px 4px 0 8px !important;
          border: none;
        }

        .vs__search {
          padding-left: 8px;
          margin: 0;
          color: $semantic-color-text-form-placeholder;
        }

        &:after {
          position: absolute;
          content: '';
          background-color: transparent;
          top: 75%;
          transform: translateY(-75%);
        }
      }
    }
  }
}

.select-scroll {
  width: 100%;
  position: relative;
  height: 40px;

  .vs__selected-options {
    position: relative;

    .vs__selected {
      margin: 8px 4px 0 100px !important;
      border: none;
    }

    .vs__search {
      padding-left: 100px;
    }

    &:after {
      position: absolute;
      content: 'LLM Preset';
      background-color: #475569;
      border-radius: 4px;
      max-width: 100px;
      max-height: 32px;
      padding: 4px 8px;
      color: #ffffff;
      font-weight: 500;
      font-size: 16px;
      line-height: 16px;
      top: 75%;
      transform: translateY(-75%);
    }
  }

  &.vs--multiple {
    .vs__selected-options {
      position: relative;

      .vs__selected {
        margin: 4px 4px 0 8px !important;
        border: none;
        border-radius: 8px;
        padding: 6px 10px;
        background-color: #e9e3f7;
      }
    }
  }
}

/* footer */
.vs-footer-loading,
.vs-footer-loadmore {
  padding: 10px 12px;
  font-size: 12px;
  color: #64748b;
}

.select-scroll-wrapper {
  &.select-scroll-hide-dropdown {
    .select-scroll .vs__actions {
      display: none;
    }
  }
}

.select-scroll-wrapper .select-scroll .vs__dropdown-menu {
  margin-top: 10px;
  border-radius: var(--vs-border-radius);
  border: 2px solid #ccc;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.select-scroll-wrapper .select-scroll .vs__dropdown-toggle {
  margin-bottom: 10px;
  border-radius: 12px;
  border: 1px solid #c6bade;
  background: white;
  cursor: text;
}

.select-scroll-wrapper .select-scroll .select-scroll-dropdown-toggle-focus {
  border: 2px solid;
  border-color: $semantic-color-form-border-focus-checked;

  .vs__selected {
    display: none;
  }
}

.select-scroll-error .vs__dropdown-toggle {
  border: 2px solid;
  border-color: $semantic-color-status-error-border-validation !important;
}

.select-scroll-wrapper {
  .scroll-option-group-label {
    font-size: 14px;
    color: #64748B;
    font-weight: 400;
    pointer-events: none;
    padding: 12px 0 8px 0;
  }

  .scroll-option-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 4px 0;
  }

  .scroll-option-icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  .scroll-option-text {
    color: #1E293B;
    font-size: 16px;
    font-weight: 400;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .scroll-selected-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .scroll-selected-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
}

/* option ที่ถูกเลือก */
.select-scroll-wrapper
  .select-scroll
  .vs__dropdown-menu
  .vs__dropdown-option--selected {
  background-color: #e5e7eb; /* gray-200 */
  color: #1f2937; /* text-gray-800 */
  font-weight: 500;
}

/* กัน hover ทับสี selected */
.select-scroll-wrapper
  .select-scroll
  .vs__dropdown-menu
  .vs__dropdown-option--selected.vs__dropdown-option--highlight {
  background-color: #d1d5db; /* gray-300 */
}
</style>
